import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Plug, Plus, Trash2, RefreshCw, Copy, Key, BarChart3, ExternalLink, BookOpen, Zap } from 'lucide-react';
import { toast } from 'sonner';

const EXAMPLE_PAYLOAD = `{
  "phone": "+48501234567",
  "email": "jan@example.com",
  "first_name": "Jan",
  "last_name": "Kowalski",
  "city": "Warszawa",
  "topic": "kredyt",
  "summary": "Klient szuka 30k na 3 lata",
  "history": "Rozmowa o RRSO. Dochod 6500 zl netto.",
  "external_id": "kasonet-lead-12345",
  "agent_hints": {
    "channels": ["sms"],
    "priority": "high",
    "call_within_hours": 24,
    "propose_offers": ["kredyt-gotowkowy"],
    "opening_line": "Panie Janie, w nawiazaniu do rozmowy...",
    "notes_for_agent": "Klient wie o RRSO, nie tlumacz od nowa",
    "lead_score": 8
  }
}`;

export default function ExternalServicesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newKey, setNewKey] = useState(null); // { api_key, service }
  const [statsFor, setStatsFor] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', description: '', default_channel: 'any', auto_agent: true, brand_name: '', landing_url_template: '' });

  const load = useCallback(async () => {
    try {
      const r = await api.get('/external-services');
      setItems(r.data.items || []);
    } catch { toast.error('Blad ladowania serwisow'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openStats = async (svcId) => {
    setStatsFor(svcId);
    setStatsData(null);
    try {
      const r = await api.get(`/external-services/${svcId}/stats`);
      setStatsData(r.data);
    } catch { toast.error('Blad ladowania statystyk'); }
  };

  const create = async () => {
    if (!form.name.trim() || !form.slug.trim()) return toast.error('Nazwa i slug wymagane');
    try {
      const r = await api.post('/external-services', form);
      setNewKey(r.data);
      setShowAdd(false);
      setForm({ name: '', slug: '', description: '', default_channel: 'any', auto_agent: true });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Blad tworzenia');
    }
  };

  const regen = async (svcId, name) => {
    if (!confirm(`Wygenerowac nowy klucz dla "${name}"? Stary klucz PRZESTANIE dzialac!`)) return;
    try {
      const r = await api.post(`/external-services/${svcId}/regenerate-key`);
      setNewKey({ api_key: r.data.api_key, service: { name, slug: '?' }, warning: r.data.warning });
    } catch { toast.error('Blad'); }
  };

  const toggleActive = async (svc) => {
    try { await api.put(`/external-services/${svc.id}`, { is_active: !svc.is_active }); load(); }
    catch { toast.error('Blad'); }
  };

  const toggleAgent = async (svc) => {
    try { await api.put(`/external-services/${svc.id}`, { auto_agent: !svc.auto_agent }); load(); }
    catch { toast.error('Blad'); }
  };

  const remove = async (svc) => {
    if (!confirm(`Usunac serwis "${svc.name}"? Kontakty z grupy "${svc.contact_group}" pozostana.`)) return;
    try { await api.delete(`/external-services/${svc.id}`); load(); }
    catch { toast.error('Blad'); }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('Klucz skopiowany');
  };

  const baseUrl = window.location.origin;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><Plug className="w-8 h-8" /> Zewnetrzne serwisy</h1>
          <p className="text-sm text-gray-500 mt-1">
            Landing page'e / partnerzy wysylajacy hot leady. Kazdy ma swoj klucz API i osobna grupe kontaktow.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <a href="/api/hot-lead/docs" target="_blank" rel="noreferrer">
              <BookOpen className="w-4 h-4 mr-2" />Pelna dokumentacja API
            </a>
          </Button>
          <Button onClick={() => setShowAdd(true)}><Plus className="w-4 h-4 mr-2" />Dodaj serwis</Button>
        </div>
      </div>

      {/* Instrukcja integracji */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="w-4 h-4 text-yellow-500" />Jak zintegrowac serwis (kasonet, doradca itp.)
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-3">
          <p>
            Zewnetrzna strona wysyla POST z hot leadem + <b>wytycznymi dla agenta</b> (kanaly, priorytet,
            oferty do zaproponowania, deadline). Agent AI bierze te leady z pierwszenstwem.
          </p>
          <div>
            <div className="text-xs font-semibold mb-1 text-gray-400">POJEDYNCZY LEAD → POST /api/hot-lead</div>
            <pre className="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto">
{`curl -X POST ${baseUrl}/api/hot-lead \\
  -H "X-API-Key: torr_svc_..." \\
  -H "Content-Type: application/json" \\
  -d '${EXAMPLE_PAYLOAD}'`}
            </pre>
          </div>
          <div>
            <div className="text-xs font-semibold mb-1 text-gray-400">WSAD (np. raport dzienny) → POST /api/hot-leads/batch</div>
            <pre className="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto">
{`curl -X POST ${baseUrl}/api/hot-leads/batch \\
  -H "X-API-Key: torr_svc_..." \\
  -H "Content-Type: application/json" \\
  -d '{"report_meta":{"report_date":"2026-07-12","sessions":4},
       "leads":[ {lead1}, {lead2}, ... ]}'`}
            </pre>
            <p className="text-xs text-gray-500 mt-1">Max 100 leadow na batch. Zwraca wynik per lead + summary (created/updated/failed).</p>
          </div>
          <div className="text-xs text-gray-500">
            Kliknij <a href="/api/hot-lead/docs" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">Pelna dokumentacja API</a> — publiczny URL, mozesz przekazac swojemu programiscie / agentowi AI.
          </div>
        </CardContent>
      </Card>

      {/* Lista serwisow */}
      {loading ? <Skeleton className="h-64" /> : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-gray-500">
          Nie masz jeszcze zadnego serwisu. Kliknij "Dodaj serwis" aby zaczac.
        </CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Serwis</TableHead>
                  <TableHead>Slug / Grupa</TableHead>
                  <TableHead>Klucz</TableHead>
                  <TableHead>Kanal</TableHead>
                  <TableHead>Auto-agent</TableHead>
                  <TableHead>Aktywny</TableHead>
                  <TableHead>Leady / Kontakty</TableHead>
                  <TableHead>Ostatni</TableHead>
                  <TableHead className="text-right">Akcje</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(s => (
                  <TableRow key={s.id}>
                    <TableCell>
                      <div className="font-medium">{s.name}</div>
                      {s.description && <div className="text-xs text-gray-500">{s.description}</div>}
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-gray-800 px-2 py-1 rounded">{s.slug}</code>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs text-gray-400">{s.api_key_preview}</code>
                    </TableCell>
                    <TableCell><Badge variant="outline">{s.default_channel}</Badge></TableCell>
                    <TableCell><Switch checked={s.auto_agent} onCheckedChange={() => toggleAgent(s)} /></TableCell>
                    <TableCell><Switch checked={s.is_active} onCheckedChange={() => toggleActive(s)} /></TableCell>
                    <TableCell>
                      <div className="text-xs">
                        <div>{s.stats?.leads_received || 0} leadow</div>
                        <div className="text-gray-500">{(s.stats?.contacts_created || 0)} nowych</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {s.last_lead_at ? new Date(s.last_lead_at).toLocaleString('pl-PL') : '—'}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="icon" variant="ghost" onClick={() => openStats(s.id)} title="Statystyki"><BarChart3 className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => regen(s.id, s.name)} title="Nowy klucz"><Key className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(s)} title="Usun"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Dialog: dodaj serwis */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle>Dodaj serwis</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nazwa</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="np. Kasonet" />
            </div>
            <div>
              <Label>Slug (grupa kontaktow)</Label>
              <Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })} placeholder="kasonet" />
              <p className="text-xs text-gray-500 mt-1">Male litery, cyfry, - lub _. Kontakty trafia do grupy o tej nazwie.</p>
            </div>
            <div>
              <Label>Opis (opcjonalny)</Label>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
            <div>
              <Label>Brand (jak agent pisze do klienta)</Label>
              <Input value={form.brand_name} onChange={e => setForm({ ...form, brand_name: e.target.value })} placeholder="Kasonet" />
              <p className="text-xs text-gray-500 mt-1">Agent pisze: "w nawiazaniu do rozmowy na <b>Kasonet</b>". Jesli puste = uzywa nazwy serwisu. NIGDY nie uzywa Torr.pl.</p>
            </div>
            <div>
              <Label>Template landing URL (opcjonalny)</Label>
              <Input value={form.landing_url_template} onChange={e => setForm({ ...form, landing_url_template: e.target.value })} placeholder="https://www.kasonet.pl/{slug}-{slug}-pl" />
              <p className="text-xs text-gray-500 mt-1">Agent nie wysyla surowego URL afiliacyjnego. Zamiast tego <code>{'{link}'}</code> w tresci → my podmieniamy na Twoj landing (kasonet.pl/nazwa-oferty). Slug oferty = auto z nazwy. Placeholder: <code>{'{slug}'}</code>.</p>
            </div>
            <div>
              <Label>Domyslny kanal</Label>
              <Select value={form.default_channel} onValueChange={v => setForm({ ...form, default_channel: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">any (agent decyduje)</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.auto_agent} onCheckedChange={v => setForm({ ...form, auto_agent: v })} />
              <Label>Agent automatycznie obsluguje leady z tego serwisu</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Anuluj</Button>
            <Button onClick={create}>Utworz + wygeneruj klucz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: nowy klucz (jednorazowo) */}
      <Dialog open={!!newKey} onOpenChange={() => setNewKey(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>🔑 Twoj klucz API</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded bg-yellow-950/40 border border-yellow-800 text-yellow-200 text-sm">
              ⚠️ {newKey?.warning || 'Zapisz klucz TERAZ — po zamknieciu tego okna nie bedzie mozna go odczytac.'}
            </div>
            <div>
              <Label>Klucz API {newKey?.service?.name ? `dla "${newKey.service.name}"` : ''}</Label>
              <div className="flex gap-2 mt-1">
                <Input value={newKey?.api_key || ''} readOnly className="font-mono text-xs" />
                <Button onClick={() => copyKey(newKey?.api_key)}><Copy className="w-4 h-4 mr-2" />Kopiuj</Button>
              </div>
            </div>
            <details className="text-xs text-gray-400">
              <summary className="cursor-pointer">Pokaz przyklad wywolania</summary>
              <pre className="mt-2 bg-gray-900 p-2 rounded overflow-x-auto">
{`curl -X POST ${baseUrl}/api/hot-lead \\
  -H "X-API-Key: ${newKey?.api_key || 'YOUR_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '${EXAMPLE_PAYLOAD}'`}
              </pre>
            </details>
          </div>
          <DialogFooter>
            <Button onClick={() => setNewKey(null)}>Zapisalem, zamknij</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: statystyki */}
      <Dialog open={!!statsFor} onOpenChange={() => { setStatsFor(null); setStatsData(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>📊 Statystyki: {statsData?.name}</DialogTitle></DialogHeader>
          {!statsData ? <Skeleton className="h-40" /> : (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="p-3 bg-gray-800 rounded"><div className="text-2xl font-bold">{statsData.stats?.leads_received || 0}</div><div className="text-xs text-gray-500">leady lacznie</div></div>
                <div className="p-3 bg-gray-800 rounded"><div className="text-2xl font-bold">{statsData.contacts_in_group || 0}</div><div className="text-xs text-gray-500">kontakty w grupie</div></div>
                <div className="p-3 bg-gray-800 rounded"><div className="text-2xl font-bold">{statsData.with_phone || 0}</div><div className="text-xs text-gray-500">z telefonem</div></div>
                <div className="p-3 bg-gray-800 rounded"><div className="text-2xl font-bold">{statsData.with_email || 0}</div><div className="text-xs text-gray-500">z emailem</div></div>
              </div>
              <div>
                <div className="text-sm font-medium mb-2">Ostatnie 20 leadow</div>
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {(statsData.recent_leads || []).map(e => (
                    <div key={e.id} className="text-xs p-2 bg-gray-800/50 rounded space-y-1">
                      <div className="flex justify-between">
                        <span>{new Date(e.created_at).toLocaleString('pl-PL')}</span>
                        <span className="flex items-center gap-2">
                          {e.event_type === 'batch_report' ? (
                            <Badge variant="secondary" className="text-xs">BATCH {e.summary?.total || '?'}</Badge>
                          ) : e.created_new_contact ? <Badge variant="default" className="text-xs">NEW</Badge> : <Badge variant="outline" className="text-xs">upd</Badge>}
                          {e.priority && ['high','urgent'].includes(e.priority) && <Badge className="text-xs bg-orange-500/20 text-orange-300">🔥 {e.priority}</Badge>}
                          {e.had_hints && <Badge className="text-xs bg-blue-500/20 text-blue-300">hints</Badge>}
                        </span>
                      </div>
                      {(e.external_id || e.channels_hint || e.propose_offers) && (
                        <div className="text-gray-500 flex gap-3 flex-wrap">
                          {e.external_id && <span>id: {e.external_id}</span>}
                          {e.channels_hint && <span>kanaly: {e.channels_hint.join(',')}</span>}
                          {e.propose_offers && <span>oferty: {e.propose_offers.join(',')}</span>}
                        </div>
                      )}
                    </div>
                  ))}
                  {(statsData.recent_leads || []).length === 0 && <div className="text-center text-gray-500 py-4">Brak leadow</div>}
                </div>
              </div>
              {statsData.contact_group && (
                <div className="text-xs">
                  <a href={`/contacts?group=${statsData.contact_group}`} className="text-blue-400 hover:underline inline-flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Zobacz kontakty w grupie "{statsData.contact_group}"
                  </a>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
